// API Service for OrderTrack Pro
// Replaces localStorage with proper database calls

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : 'https://slvppapi.nakki.biz/api';

// Generic API helper
async function apiCall(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || `HTTP error! status: ${response.status}`);
    }
    
    return await response.json();
  } catch (error) {
    console.error('API call failed:', error);
    throw error;
  }
}

// =============== ORDER SERVICES ===============

export const orderService = {
  // Get all orders
  async getAllOrders() {
    return await apiCall('/orders');
  },

  // Get single order by Order Number (ORD-xxx) - PRIMARY METHOD
  async getOrderByNumber(orderNumber) {
    return await apiCall(`/orders/${orderNumber}`);
  },

  // Get single order by internal ID (backward compatibility)
  async getOrderById(id) {
    return await apiCall(`/orders/by-id/${id}`);
  },

  // Create new order
  async createOrder(orderData) {
    return await apiCall('/orders', {
      method: 'POST',
      body: JSON.stringify(orderData),
    });
  },

  // Update order by Order Number (ORD-xxx) - PRIMARY METHOD
  async updateOrderByNumber(orderNumber, orderData) {
    return await apiCall(`/orders/${orderNumber}`, {
      method: 'PUT',
      body: JSON.stringify(orderData),
    });
  },

  // Delete order by Order Number (ORD-xxx) - PRIMARY METHOD
  async deleteOrderByNumber(orderNumber) {
    return await apiCall(`/orders/${orderNumber}`, {
      method: 'DELETE',
    });
  },

  // Dispatch management by Order Number
  async addDispatchToOrder(orderNumber, dispatchData) {
    return await apiCall(`/orders/${orderNumber}/dispatches`, {
      method: 'POST',
      body: JSON.stringify(dispatchData),
    });
  },

  async updateDispatch(orderNumber, dispatchId, dispatchData) {
    return await apiCall(`/orders/${orderNumber}/dispatches/${dispatchId}`, {
      method: 'PUT',
      body: JSON.stringify(dispatchData),
    });
  },

  async deleteDispatch(orderNumber, dispatchId) {
    return await apiCall(`/orders/${orderNumber}/dispatches/${dispatchId}`, {
      method: 'DELETE',
    });
  },

  // Order notes by Order Number
  async addNoteToOrder(orderNumber, noteData) {
    return await apiCall(`/orders/${orderNumber}/notes`, {
      method: 'POST',
      body: JSON.stringify(noteData),
    });
  },

  async getOrderNotes(orderNumber) {
    return await apiCall(`/orders/${orderNumber}/notes`);
  }
};

// =============== BRAND SPECS SERVICES ===============

export const brandSpecService = {
  // Get all brand specifications
  async getAllBrandSpecs() {
    return await apiCall('/brand-specs');
  },

  // Get single brand spec by brandId
  async getBrandSpecById(brandId) {
    return await apiCall(`/brand-specs/${brandId}`);
  },

  // Get single brand spec by brand name
  async getBrandSpecByName(brandName) {
    return await apiCall(`/brand-specs/by-name/${encodeURIComponent(brandName)}`);
  },

  // Create new brand specification
  async createBrandSpec(brandData) {
    return await apiCall('/brand-specs', {
      method: 'POST',
      body: JSON.stringify(brandData),
    });
  },

  // Update brand specification
  async updateBrandSpec(brandId, brandData) {
    return await apiCall(`/brand-specs/${brandId}`, {
      method: 'PUT',
      body: JSON.stringify(brandData),
    });
  },

  // Delete brand specification
  async deleteBrandSpec(brandId) {
    return await apiCall(`/brand-specs/${brandId}`, {
      method: 'DELETE',
    });
  },

  // Get brand names for dropdown (helper method)
  async getBrandNames() {
    const brands = await this.getAllBrandSpecs();
    return brands.map(brand => ({
      id: brand.brandId,
      value: brand.brandName,
      label: `${brand.brandName} (${brand.brandCode}) - ${brand.buyerCompany}`
    }));
  }
};

// =============== USER SERVICES ===============

export const userService = {
  // Get all users
  async getAllUsers() {
    return await apiCall('/users');
  },

  // Create new user
  async createUser(userData) {
    return await apiCall('/users', {
      method: 'POST',
      body: JSON.stringify(userData),
    });
  },

  // Login
  async login(username, password) {
    return await apiCall('/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  }
};

// =============== UTILITY FUNCTIONS ===============

// Clear all data (for testing)
export async function clearAllData() {
  // This would need to be implemented as admin endpoints
  console.warn('clearAllData not implemented for database version');
}

// Check API connection
export async function checkConnection() {
  try {
    const response = await apiCall('/');
    return { connected: true, message: response.message };
  } catch (error) {
    return { connected: false, error: error.message };
  }
}

// Export default API configuration
export default {
  orderService,
  brandSpecService,
  userService,
  clearAllData,
  checkConnection
};